import { useState, useMemo } from 'react';
import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	Popover,
	MenuItem,
	FormControlLabel,
	Checkbox,
	Button
} from '@mui/material';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import ExpandableTableRow from './ExpandableTableRow';
import IdleFilterIcon from '../../assets/filter_idle.svg';
import SortAIcon from '../../assets/appliedsortA-Z.svg';
import SortZIcon from '../../assets/appliedsortZ-A.svg';

interface SpendTableData {
	name: string;
	totalSpent: number;
	percentage: number;
	children?: SpendTableData[];
}

interface SpendTableProps {
	title: string;
	data: SpendTableData[];
	firstColumnLabel?: string;
}

type ColumnKey = 'name' | 'totalSpent' | 'percentage';
type SortDirection = 'asc' | 'desc' | null;
type FilterState = {
	[key in ColumnKey]?: string[];
};

const SpendTable = ({ title, data, firstColumnLabel = 'Name' }: SpendTableProps) => {
	const [filterAnchor, setFilterAnchor] = useState<{ column: ColumnKey; element: HTMLElement } | null>(null);
	const [filters, setFilters] = useState<FilterState>({});
	const [sortColumn, setSortColumn] = useState<ColumnKey | null>(null);
	const [sortDirection, setSortDirection] = useState<SortDirection>(null);

	const uniqueValues = useMemo(() => {
		const values: { [key in ColumnKey]?: string[] } = {
			name: Array.from(new Set(data.map((item) => item.name))).sort(),
			totalSpent: Array.from(new Set(data.map((item) => item.totalSpent.toString()))).sort(),
			percentage: Array.from(new Set(data.map((item) => item.percentage.toFixed(2)))).sort()
		};
		return values;
	}, [data]);

	const filteredAndSortedData = useMemo(() => {
		let filtered = [...data];

		if (filters.name && filters.name.length > 0) {
			filtered = filtered.filter((item) => filters.name!.includes(item.name));
		}
		if (filters.totalSpent && filters.totalSpent.length > 0) {
			filtered = filtered.filter((item) => filters.totalSpent!.includes(item.totalSpent.toString()));
		}
		if (filters.percentage && filters.percentage.length > 0) {
			filtered = filtered.filter((item) => filters.percentage!.includes(item.percentage.toFixed(2)));
		}

		if (sortColumn && sortDirection) {
			filtered.sort((a, b) => {
				const aVal = a[sortColumn];
				const bVal = b[sortColumn];
				if (sortDirection === 'asc') {
					return aVal > bVal ? 1 : -1;
				} else {
					return aVal < bVal ? 1 : -1;
				}
			});
		}

		return filtered;
	}, [data, filters, sortColumn, sortDirection]);

	const handleFilterClose = () => {
		setFilterAnchor(null);
	};

	const handleFilterChange = (column: ColumnKey, value: string, checked: boolean) => {
		setFilters((prev) => {
			const currentFilters = prev[column] || [];
			if (checked) {
				return { ...prev, [column]: [...currentFilters, value] };
			} else {
				return { ...prev, [column]: currentFilters.filter((v) => v !== value) };
			}
		});
	};

	const handleSelectAll = (column: ColumnKey, checked: boolean) => {
		if (checked) {
			setFilters((prev) => ({ ...prev, [column]: undefined }));
		} else {
			setFilters((prev) => ({ ...prev, [column]: [] }));
		}
	};

	const handleClearFilters = () => {
		setFilters({});
		handleFilterClose();
	};

	const handleSort = (column: ColumnKey) => {
		if (sortColumn === column) {
			if (sortDirection === 'asc') {
				setSortDirection('desc');
			} else if (sortDirection === 'desc') {
				setSortColumn(null);
				setSortDirection(null);
			}
		} else {
			setSortColumn(column);
			setSortDirection('asc');
		}
	};

	const getSortIcon = (column: ColumnKey) => {
		if (sortColumn !== column) {
			return <img alt="Filter icon" src={IdleFilterIcon} style={{ width: 16, height: 16 }} />;
		}
		return sortDirection === 'asc' ? (
			<img alt="Sort icon" src={SortAIcon} style={{ width: 16, height: 16 }} />
		) : (
			<img alt="Sort icon" src={SortZIcon} style={{ width: 16, height: 16 }} />
		);
	};

	return (
		<>
			<Typography color="primary" variant='widgetTitle'>
				<HighlightedText text={title} />
			</Typography>
			<TableContainer sx={{ bgcolor: 'transparent', mt: 1 }}>
				<Table sx={{ bgcolor: 'transparent' }}>
					<TableHead>
						<TableRow>
							<TableCell
								onClick={() => handleSort('name')}
								sx={{
									'&.MuiTableCell-root': {
										color: 'white.main',
										bgcolor: 'primary.main',
										backgroundColor: (theme) => `${theme.palette.primary.main} !important`,
										borderRight: (theme) => `1px solid ${theme.palette.white.main} !important`,
										padding: '8px 12px !important',
										width: 'auto',
										cursor: 'pointer',
										borderStartStartRadius: '6px',
									}
								}}
							>
								<Typography variant='tableRowFont' display="flex" alignItems="center" justifyContent="space-between" width="100%">
									<HighlightedText text={firstColumnLabel} />
									{getSortIcon('name')}
								</Typography>
							</TableCell>
							<TableCell
								onClick={() => handleSort('totalSpent')}
								sx={{
									'&.MuiTableCell-root': {
										color: 'white.main',
										bgcolor: 'primary.main',
										backgroundColor: (theme) => `${theme.palette.primary.main} !important`,
										borderRight: (theme) => `1px solid ${theme.palette.white.main} !important`,
										padding: '8px 12px !important',
										width: '200px',
										cursor: 'pointer'
									}
								}}
							>
								<Typography variant='tableRowFont' display="flex" alignItems="center" justifyContent="space-between" width="100%">
									<HighlightedText text="Spend" />
									{getSortIcon('totalSpent')}
								</Typography>
							</TableCell>
							<TableCell
								onClick={() => handleSort('percentage')}
								sx={{
									'&.MuiTableCell-root': {
										color: 'white.main',
										bgcolor: 'primary.main',
										backgroundColor: (theme) => `${theme.palette.primary.main} !important`,
										padding: '8px 12px !important',
										width: '200px',
										cursor: 'pointer',
										borderStartEndRadius: '6px',
									}
								}}
							>
								<Typography variant='tableRowFont' display="flex" alignItems="center" justifyContent="space-between" width="100%">
									<HighlightedText text="% of Total" />
									{getSortIcon('percentage')}
								</Typography>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{filteredAndSortedData.map((row, idx) => (
							<ExpandableTableRow key={idx} row={row} index={idx} />
						))}
					</TableBody>
				</Table>
			</TableContainer>

			<Popover
				open={Boolean(filterAnchor)}
				anchorEl={filterAnchor?.element}
				onClose={handleFilterClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left'
				}}
			>
				<Box sx={{ p: 2, minWidth: 200 }}>
					{filterAnchor && (
						<>
							<MenuItem>
								<FormControlLabel
									label={<Typography variant="filterText">All</Typography>}
									control={
										<Checkbox
											checked={!filters[filterAnchor.column] || filters[filterAnchor.column]!.length === 0}
											onChange={(e) => handleSelectAll(filterAnchor.column, e.target.checked)}
										/>
									}
								/>
							</MenuItem>
							{uniqueValues[filterAnchor.column]?.map((value) => (
								<MenuItem key={value}>
									<FormControlLabel
										label={<Typography variant="filterText">{value}</Typography>}
										control={
											<Checkbox
												checked={
													!filters[filterAnchor.column] ||
													filters[filterAnchor.column]!.length === 0 ||
													filters[filterAnchor.column]!.includes(value)
												}
												onChange={(e) => handleFilterChange(filterAnchor.column, value, e.target.checked)}
											/>
										}
									/>
								</MenuItem>
							))}
							<Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
								<Button size="small" onClick={handleClearFilters}>
									Clear
								</Button>
							</Box>
						</>
					)}
				</Box>
			</Popover>
		</>
	);
};

export default SpendTable;
